import DialogUtils from "../DialogUtils.js";

export default class SubstanceDialogHtml {
    #dialog;
    #callbacks = {
        onOkBtnClick: [],
        onAliasInput: [],
        onMfOrEmwInput: [],
        onCancel: [],
        onDeleteBtnClick: [],
        onMassSpecBtnClick: [],
        onColorPickerClick: []
    }
    #aliasListToBeSetOnFirstInput = [];

    /**
     * @param {SubstanceDialogFormData} formData
     */
    show(formData) {
        const dialogEl = this.#getDialogEl();
        dialogEl.innerHTML = this.#template(formData);
        this.fillInputs(formData);
        this.#setEventListeners(formData.isReadOnly());
        this.setVisible(true);
    }

    remove() {
        this.#dialog.remove();
        this.#dialog = null;
    }

    setSubstanceColor(color) {
        this.#getColorPreviewEl().style.backgroundColor = color;
    }

    showAliasFieldError(text) {
        this.#getAliasInputEl().errorMessage = text;
    }

    showMfOrEmwFieldError(text) {
        this.#getMfOrEmwInputEl().errorMessage = text;
    }

    setVisible(isVisible) {
        if (this.#dialog == null)
            // The dialog could've been removed while an async action was in flight,
            // so there's nothing to show or hide anymore.
            return;
        isVisible ? this.#dialog.showModal() : this.#dialog.close()
    }

    /** @param {SubstanceDialogFormData} formData */
    fillInputs(formData) {
        const {alias, mfOrEmw, struct} = formData;
        const aliasInputEl = this.#getAliasInputEl();
        aliasInputEl.value = {displayName: alias || ""};
        if (mfOrEmw != null)
            this.#getMfOrEmwInputEl().value = mfOrEmw;
        this.#getMfOrEmwInputEl().setDisabled(formData.isReadOnly() || struct != null);
        if (formData.color)
            this.setSubstanceColor(formData.color);
        this.#aliasListToBeSetOnFirstInput = formData.aliasAutocompleteList;
    }

    setMfOrEmwInputDisabled(disabled) {
        if (this.#dialog != null)
            this.#getMfOrEmwInputEl().setDisabled(disabled);
    }

    setAsyncTaskInProgress(inProgress) {
        if (this.#dialog == null)
            return;
        this.#getAliasInputEl().setDisabled(inProgress);
        this.#getMfOrEmwInputEl().setDisabled(inProgress);
        const okButton = this.#getOkButtonEl();
        if (okButton)
            okButton.toggleAttribute("disabled", inProgress);
        const deleteButtonEl = this.#getDeleteButtonEl();
        if (deleteButtonEl)
            deleteButtonEl.toggleAttribute("disabled", inProgress);
    }

    getStructureComponentParentEl() {
        return this.#getDialogEl().querySelector("upload-structure-component");
    }

    onAliasInput(callback) {
        this.#callbacks.onAliasInput.push(callback);
    }

    onMfOrEmwInput(callback) {
        this.#callbacks.onMfOrEmwInput.push(callback);
    }

    onOkBtnClick(callback) {
        this.#callbacks.onOkBtnClick.push(callback);
    }

    onCancel(callback) {
        this.#callbacks.onCancel.push(callback);
    }

    onDeleteBtnClick(callback) {
        this.#callbacks.onDeleteBtnClick.push(callback);
    }

    onMassSpecBtnClick(callback) {
        this.#callbacks.onMassSpecBtnClick.push(callback);
    }

    onColorPickerClick(callback) {
        this.#callbacks.onColorPickerClick.push(callback);
    }

    #getDialogEl() {
        if (!this.#dialog) {
            const dialog = document.body
                .insertAdjacentElement("beforeend", document.createElement('dialog'));
            dialog.classList.add('substance-dialog');
            this.#dialog = dialog
        }
        return this.#dialog;
    }

    #getAliasInputEl() {
        return this.#dialog.querySelector("#alias");
    }

    #getMfOrEmwInputEl() {
        return this.#dialog.querySelector("#mfOrEmw");
    }

    #getOkButtonEl() {
        return this.#dialog.querySelector(".btn-ok");
    }

    #getMassSpecButtonEl() {
        return this.#dialog.querySelector(".button-mass-spec");
    }

    #getDeleteButtonEl() {
        return this.#dialog.querySelector(".btn-delete");
    }

    #getSubstanceColorContainer() {
        return this.#dialog.querySelector("#substance-dialog-color");
    }

    /** @returns {ColorPalette} */
    getColorPalette() {
        return this.#dialog.querySelector("color-palette");
    }

    #getColorPreviewEl() {
        return this.#dialog.querySelector(".color-preview-rect");
    }

    /**
     * @param {boolean} isReadOnlyMode
     */
    #setEventListeners(isReadOnlyMode) {
        DialogUtils.setDefaultDialogListeners(this.#dialog);
        // handle closing
        this.#dialog.addEventListener("cancel", this.#onEscOrCancel.bind(this));
        const getMassSpecButtonEl = this.#getMassSpecButtonEl();
        if (getMassSpecButtonEl)
            getMassSpecButtonEl.addEventListener("click", this.#onMassSpecBtnClick.bind(this));
        if (isReadOnlyMode)
            return;
        // triggered everytime when user is typing into this input
        this.#getAliasInputEl().addEventListener("input", this.#onAliasInput.bind(this));
        // triggered when user clicks on dropdown
        this.#getAliasInputEl().onChangeValue(this.#onAliasInput.bind(this));
        this.#getMfOrEmwInputEl().onInput(this.#onMfOrEmwInput.bind(this));
        const okButton = this.#getOkButtonEl();
        if (okButton)
            okButton.addEventListener("click", this.#onOk.bind(this));
        const deleteButton = this.#getDeleteButtonEl();
        if (deleteButton)
            deleteButton.addEventListener("click", this.#onDelete.bind(this));
        this.#getSubstanceColorContainer().addEventListener("click", this.#onColorClick.bind(this));
    }

    #onAliasInput() {
        const aliasInputEl = this.#getAliasInputEl();
        const autocompleteUpdated = this.#setAliasAutocompleteListIfNeeded();
        if (autocompleteUpdated) {
            aliasInputEl.dispatchEvent(new Event("focus"));
            return;
        }
        const alias = aliasInputEl.rawValue;
        this.#callbacks.onAliasInput.forEach(cb => cb(alias))
    }

    #onMfOrEmwInput() {
        const mfOrEmw = this.#getMfOrEmwInputEl().value;
        this.#callbacks.onMfOrEmwInput.forEach(cb => cb(mfOrEmw))
    }

    #onOk() {
        if (this.#validateInputs())
            this.#callbacks.onOkBtnClick.forEach(cb => cb())
    }

    #onDelete() {
        this.#callbacks.onDeleteBtnClick.forEach(cb => cb())
    }

    #onMassSpecBtnClick() {
        if (this.#validateInputs())
            this.#callbacks.onMassSpecBtnClick.forEach(cb => cb())
    }

    #onColorClick() {
        this.#callbacks.onColorPickerClick.forEach(cb => cb());
    }

    #onEscOrCancel(evt) {
        evt.preventDefault();
        this.#callbacks.onCancel.forEach(cb => cb());
    }

    #validateInputs() {
        return this.#getAliasInputEl().checkValidity() && this.#getMfOrEmwInputEl().checkValidity();
    }

    #setAliasAutocompleteListIfNeeded() {
        if (this.#aliasListToBeSetOnFirstInput.length > 0) {
            this.#getAliasInputEl().options = this.#aliasListToBeSetOnFirstInput;
            this.#aliasListToBeSetOnFirstInput = [];
            return true;
        }
        return false;
    }

    /**
     * @param {SubstanceDialogFormData} formData
     * @returns {string}
     * @private
     */
    #template(formData) {
        const isReadOnly = formData.isReadOnly();
        const okBtnTitle = formData.id ? 'Save' : 'Create';
        const disabledAttr = isReadOnly ? 'disabled' : '';
        return `
            <div class="progress"><div class="indeterminate"></div></div>
            <div class="dialog-content">
         	    <div class="dialog-content-col col-1">
                    <typeahead-input 
                        id="alias" 
                        label="Alias"
                        no-icon
                        maxlength="100"
                        ${disabledAttr}>
                    </typeahead-input>
                    <text-input
                        id="mfOrEmw" 
                        label="Chem Formula or EMW"
                        required
                        maxlength="100"
                        ${disabledAttr}>
                    </text-input>
                    <div id="substance-dialog-color" ${disabledAttr}>
                            <span>Chromatogram color: </span>
                            <div class="color-preview-rect" style="background-color: ${formData.color}"></div>
                            <color-palette hidden></color-palette>
                    </div>
         	    </div>
                <div class="dialog-content-col col-2">
                    <upload-structure-component></upload-structure-component>
                </div>
            </div>
            <div class="dialog-buttons-row">
            	<div class="justify-start">
                	${(formData.id && !isReadOnly) ? '<button class="button button--text button--text-red btn-delete">Delete</button>' : ''}
                	${formData.massSpecEnabled ? '<button class="button button--text button-mass-spec">Mass Spec settings</button>' : ''}					    	
                </div>
                	<button class="button button--text btn-cancel">Close</button>
                    ${isReadOnly? '': `<button class="button button--contained btn-ok">${okBtnTitle}</button>`}
            </div>`;
    }
}